import { Z_INDEX } from '@constants/z-index';
import { Drawer, SwipeableDrawer } from '@mui/material';
import classNames from 'classnames';
import { ReactNode } from 'react';
import css from './MobileDrawer.module.scss';

// Sits above MUI Dialog/Modal (1300). Applied unconditionally so a drawer
// always overlays the surface that launched it — opt-in z-index was a
// footgun callers kept forgetting when nesting drawers inside modals.
//
// Sourced from Z_INDEX rather than declared here so that surfaces which must
// clear a drawer (Z_INDEX.aboveMobileDrawer) are derived from the same number
// and cannot silently desync from it.
const DRAWER_Z_INDEX = Z_INDEX.mobileDrawer;

type MobileDrawerProps = {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  children: ReactNode;
  /** Use a frosted glass background instead of the default opaque dark background */
  frosted?: boolean;
  /** Remove backdrop blur (keep the dim). Use when layered over another blurred surface. */
  noBackdropBlur?: boolean;
  /** Called after the drawer's opening transition finishes. */
  onSettled?: () => void;
  /** Called after the drawer's closing transition finishes. */
  onClosed?: () => void;
  /** Disable swipe-to-dismiss (locks the drawer open until closed programmatically). */
  disableSwipe?: boolean;
  /**
   * Override the drawer's z-index. Defaults to {@link DRAWER_Z_INDEX} (1400, above
   * modals). Pass a lower value for the rare drawer that LAUNCHES modals which should
   * appear above it (e.g. the mobile Add Media drawer → `Z_INDEX.drawer`).
   */
  zIndex?: number;
};

const backdropSx = (noBackdropBlur?: boolean) =>
  noBackdropBlur
    ? { backgroundColor: 'rgba(0, 0, 0, 0.3)' }
    : { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.5)' };

export const MobileDrawer = ({
  open,
  onClose,
  onOpen,
  children,
  frosted,
  noBackdropBlur,
  onSettled,
  onClosed,
  disableSwipe,
  zIndex,
}: MobileDrawerProps) => {
  const content = (
    <div className={classNames(css.drawerContent, { [css.frosted]: frosted })}>
      {!disableSwipe && <div className={css.handle} />}
      {children}
    </div>
  );

  const zIndexSx = { zIndex: zIndex ?? DRAWER_Z_INDEX };

  if (disableSwipe) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        className={css.mobileDrawer}
        sx={zIndexSx}
        slotProps={{
          transition: { onEntered: onSettled, onExited: onClosed },
          backdrop: { sx: backdropSx(noBackdropBlur) },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      // These sheets open only via their own triggers. Without this, MUI's
      // edge swipe-to-open zone (~20px off the bottom) can pop a drawer open on
      // an upward drag started while scrolling content near the bottom edge.
      disableSwipeToOpen
      className={css.mobileDrawer}
      sx={zIndexSx}
      slotProps={{
        transition: { onEntered: onSettled, onExited: onClosed },
        backdrop: { sx: backdropSx(noBackdropBlur) },
      }}
    >
      {content}
    </SwipeableDrawer>
  );
};
